import {
  BadgeCheck,
  CalendarDays,
  ChevronRight,
  Clock3,
  ImageDown,
  MapPin,
  Radio,
  ShieldCheck,
  Swords,
  UserRound,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ClubLogo } from './ClubLogo';
import { MatchdayMediaStudio } from './MatchdayMediaStudio';
import { getNextOfficialFixture, officialOpponent, officialVenueLabel } from './officialFixtures';
import { resolveBundledClub } from './leagueClubs';
import { useData } from './DataContext';
import { useI18n } from './I18nContext';
import type { Lineup, Match } from './types';
import type { ModuleId } from './AppShell';

type MatchdayLink = {
  matchId: string;
  lineupId: string;
  lineupName: string;
  formation: string;
  announced: boolean;
  captainId: string;
  viceCaptainId: string;
  substitutes: string[];
  status: 'planning' | 'announced' | 'live' | 'complete';
  notes: string;
  referee: string;
  venue: string;
  updatedAt: string;
};

function storageKey(teamId: string, matchId: string) {
  return `ecc_matchday_command_${teamId}_${matchId}`;
}

function lineupMetaKey(teamId: string, lineupName: string) {
  return `ecc_lineup_meta_${teamId}_${lineupName.trim().toLowerCase().replace(/\s+/g, '-')}`;
}

function readLineupMeta(teamId: string, lineupName: string) {
  try {
    return JSON.parse(localStorage.getItem(lineupMetaKey(teamId, lineupName)) || '{}') as {
      captainId?: string;
      viceCaptainId?: string;
      announced?: boolean;
    };
  } catch {
    return {};
  }
}

export function MatchdayCommandCentre({
  onNavigate,
}: {
  onNavigate: (module: ModuleId) => void;
}) {
  const { t } = useI18n();
  const { team, players, matches, lineups, loading, error } = useData();

  const orderedMatches = useMemo(
    () =>
      [...matches]
        .filter((match) => new Date(match.match_date).getTime() >= Date.now() - 86400000)
        .sort((a, b) => a.match_date.localeCompare(b.match_date)),
    [matches],
  );

  const officialFixture = getNextOfficialFixture();

  const fallbackMatch: Match | null = !orderedMatches.length && officialFixture && team
    ? {
        id: `official-${officialFixture.round}`,
        team_id: team.id,
        match_date: `${officialFixture.date}T12:00:00`,
        opponent: officialOpponent(officialFixture),
        competition: officialFixture.competition,
        goals_for: null,
        goals_against: null,
        status: 'scheduled',
      }
    : null;

  const matchOptions = fallbackMatch ? [fallbackMatch] : orderedMatches;

  const [selectedMatchId, setSelectedMatchId] = useState('');
  const selectedMatch =
    matchOptions.find((match) => match.id === selectedMatchId) ||
    matchOptions[0] ||
    null;

  const [selectedLineupId, setSelectedLineupId] = useState('');
  const selectedLineup =
    lineups.find((lineup) => lineup.id === selectedLineupId) ||
    lineups[0] ||
    null;

  const [link, setLink] = useState<MatchdayLink | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!selectedMatchId && matchOptions[0]) setSelectedMatchId(matchOptions[0].id);
  }, [matchOptions, selectedMatchId]);

  useEffect(() => {
    if (!selectedLineupId && lineups[0]) setSelectedLineupId(lineups[0].id);
  }, [lineups, selectedLineupId]);

  useEffect(() => {
    if (!team || !selectedMatch) {
      setLink(null);
      return;
    }

    try {
      const raw = localStorage.getItem(storageKey(team.id, selectedMatch.id));
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<MatchdayLink>;
        setLink({
          matchId: parsed.matchId || selectedMatch.id,
          lineupId: parsed.lineupId || selectedLineup?.id || '',
          lineupName: parsed.lineupName || selectedLineup?.name || '',
          formation: parsed.formation || selectedLineup?.formation || '—',
          announced: Boolean(parsed.announced),
          captainId: parsed.captainId || '',
          viceCaptainId: parsed.viceCaptainId || '',
          substitutes: Array.isArray(parsed.substitutes) ? parsed.substitutes : [],
          status: parsed.status || 'planning',
          notes: parsed.notes || '',
          referee: parsed.referee || 'TBA',
          venue: parsed.venue || (
            selectedMatch.id.startsWith('official-') && officialFixture
              ? officialVenueLabel(officialFixture)
              : ''
          ),
          updatedAt: parsed.updatedAt || new Date().toISOString(),
        });
        return;
      }
    } catch {
      // Safe local fallback.
    }

    if (selectedLineup) {
      const meta = readLineupMeta(team.id, selectedLineup.name);
      setLink({
        matchId: selectedMatch.id,
        lineupId: selectedLineup.id,
        lineupName: selectedLineup.name,
        formation: selectedLineup.formation || '—',
        announced: Boolean(meta.announced),
        captainId: meta.captainId || '',
        viceCaptainId: meta.viceCaptainId || '',
        substitutes: [],
        status: meta.announced ? 'announced' : 'planning',
        notes: '',
        referee: 'TBA',
        venue: selectedMatch.id.startsWith('official-') && officialFixture
          ? officialVenueLabel(officialFixture)
          : '',
        updatedAt: new Date().toISOString(),
      });
    } else {
      setLink(null);
    }
  }, [selectedLineup, selectedMatch, team]);

  function saveLink(next: MatchdayLink) {
    if (!team || !selectedMatch) return;
    const payload = { ...next, updatedAt: new Date().toISOString() };
    setLink(payload);
    try {
      localStorage.setItem(storageKey(team.id, selectedMatch.id), JSON.stringify(payload));
      setMessage(t.matchdayCommandSaved);
    } catch {
      setMessage(t.savedOnDevice);
    }
  }

  function attachLineup() {
    if (!selectedLineup || !selectedMatch || !team) return;
    const meta = readLineupMeta(team.id, selectedLineup.name);
    saveLink({
      matchId: selectedMatch.id,
      lineupId: selectedLineup.id,
      lineupName: selectedLineup.name,
      formation: selectedLineup.formation || '—',
      announced: Boolean(meta.announced),
      captainId: meta.captainId || '',
      viceCaptainId: meta.viceCaptainId || '',
      substitutes: link?.substitutes || [],
      status: meta.announced ? 'announced' : 'planning',
      notes: link?.notes || '',
      referee: link?.referee || 'TBA',
      venue: link?.venue || (
        selectedMatch.id.startsWith('official-') && officialFixture
          ? officialVenueLabel(officialFixture)
          : ''
      ),
      updatedAt: new Date().toISOString(),
    });
  }

  function setStatus(status: MatchdayLink['status']) {
    if (!link) return;
    saveLink({
      ...link,
      status,
      announced: status === 'announced' || status === 'live' || status === 'complete'
        ? true
        : link.announced,
    });
  }

  const captain = players.find((player) => player.id === link?.captainId);
  const viceCaptain = players.find((player) => player.id === link?.viceCaptainId);
  const lineupSlots = (selectedLineup?.slots || {}) as Record<string, string | null>;
  const starterIds = new Set(Object.values(lineupSlots).filter(Boolean) as string[]);
  const startingXI = players.filter((player) => starterIds.has(player.id));

  const readinessChecks = [
    { label: t.matchSelected, ready: Boolean(selectedMatch) },
    { label: t.lineupAttached, ready: Boolean(link?.lineupId) },
    { label: t.startingXI, ready: startingXI.length === 11 },
    { label: t.captain, ready: Boolean(link?.captainId) },
    { label: t.viceCaptain, ready: Boolean(link?.viceCaptainId) },
    { label: t.referee, ready: Boolean(link?.referee && link.referee.trim() && link.referee !== 'TBA') },
    { label: t.venue, ready: Boolean(link?.venue && link.venue.trim()) },
  ];
  const readinessCount = readinessChecks.filter((item) => item.ready).length;
  const readinessPercent = Math.round((readinessCount / readinessChecks.length) * 100);

  if (loading) return <section className="metal-card loading-panel">{t.loading}…</section>;
  if (error) return <section className="metal-card error-panel">{error}</section>;

  return (
    <div className="matchday-command-page">
      <section className="metal-card matchday-command-hero">
        <div>
          <p className="eyebrow">MATCHDAY COMMAND CENTRE</p>
          <h1>{t.matchdayCommandCentre}</h1>
          <p className="muted">{t.matchdayCommandSubtitle}</p>
        </div>

        <div className="matchday-command-selectors">
          <label>
            <span>{t.matchday}</span>
            <select value={selectedMatch?.id || ''} onChange={(event) => setSelectedMatchId(event.target.value)}>
              {matchOptions.map((match) => (
                <option value={match.id} key={match.id}>
                  {new Date(match.match_date).toLocaleDateString()} · {match.opponent || '—'}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>{t.savedLineups}</span>
            <select value={selectedLineup?.id || ''} onChange={(event) => setSelectedLineupId(event.target.value)}>
              {lineups.map((lineup) => (
                <option value={lineup.id} key={lineup.id}>
                  {lineup.name} · {lineup.formation || '—'}
                </option>
              ))}
            </select>
          </label>

          <button className="gold-button" onClick={attachLineup} disabled={!selectedMatch || !selectedLineup}>
            <ShieldCheck size={17} />
            {t.attachLineup}
          </button>
        </div>

        {message && <p className="form-message">{message}</p>}
      </section>

      {selectedMatch ? (
        <>
          <section className="metal-card matchday-match-identity">
            <div className="matchday-team">
              <ClubLogo club="Polonia Glasgow" className="matchday-command-logo" />
              <strong>Polonia Glasgow AFC</strong>
            </div>

            <div className="matchday-vs-core">
              <span>{new Date(selectedMatch.match_date).toLocaleDateString()}</span>
              <strong>VS</strong>
              <small>{selectedMatch.competition || '—'}</small>
            </div>

            <div className="matchday-team">
              <ClubLogo club={selectedMatch.opponent} className="matchday-command-logo" />
              <strong>{resolveBundledClub(selectedMatch.opponent)?.displayName || selectedMatch.opponent || '—'}</strong>
            </div>
          </section>

          <section className="matchday-command-grid">
            <article className="metal-card command-status-card">
              <div className="section-title-line">
                <div>
                  <p className="eyebrow">{t.matchdayStatus}</p>
                  <h2>{link ? t[link.status] : t.planning}</h2>
                </div>
                <Radio size={24} />
              </div>

              <div className="matchday-status-buttons">
                {(['planning', 'announced', 'live', 'complete'] as const).map((status) => (
                  <button
                    key={status}
                    className={link?.status === status ? 'active' : ''}
                    onClick={() => setStatus(status)}
                    disabled={!link}
                  >
                    {t[status]}
                  </button>
                ))}
              </div>

              <div className="matchday-facts">
                <span><CalendarDays size={15} /> {new Date(selectedMatch.match_date).toLocaleString()}</span>
                <span><Swords size={15} /> {selectedMatch.competition || '—'}</span>
                <label className="matchday-inline-fact">
                  <MapPin size={15} />
                  <input
                    value={link?.venue || ''}
                    placeholder={t.venue}
                    onChange={(event) => link && setLink({ ...link, venue: event.target.value })}
                    onBlur={() => link && saveLink(link)}
                  />
                </label>
                <label className="matchday-inline-fact">
                  <UserRound size={15} />
                  <input
                    value={link?.referee || ''}
                    placeholder={`${t.referee}: TBA`}
                    onChange={(event) => link && setLink({ ...link, referee: event.target.value })}
                    onBlur={() => link && saveLink(link)}
                  />
                </label>
              </div>
            </article>

            <article className="metal-card lineup-command-card">
              <div className="section-title-line">
                <div>
                  <p className="eyebrow">{t.attachedLineup}</p>
                  <h2>{link?.lineupName || t.noSavedLineups}</h2>
                </div>
                <BadgeCheck size={24} />
              </div>

              <div className="lineup-command-metrics">
                <div><span>{t.formation}</span><strong>{link?.formation || '—'}</strong></div>
                <div><span>{t.startingXI}</span><strong>{startingXI.length}/11</strong></div>
                <div><span>{t.captain}</span><strong>{captain?.name || '—'}</strong></div>
                <div><span>{t.viceCaptain}</span><strong>{viceCaptain?.name || '—'}</strong></div>
              </div>
            </article>
          </section>

          <section className="metal-card matchday-readiness-card">
            <div className="section-title-line">
              <div>
                <p className="eyebrow">{t.matchdayReadiness}</p>
                <h2>{readinessPercent}%</h2>
              </div>
              <ShieldCheck size={25} />
            </div>
            <div className="matchday-readiness-bar"><i style={{ width: `${readinessPercent}%` }} /></div>
            <div className="matchday-readiness-grid">
              {readinessChecks.map((item) => (
                <span className={item.ready ? 'ready' : 'pending'} key={item.label}>
                  {item.ready ? '✓' : '○'} {item.label}
                </span>
              ))}
            </div>
          </section>

          <section className="metal-card matchday-starting-xi">
            <div className="section-title-line">
              <div>
                <p className="eyebrow">{t.startingXI}</p>
                <h2>{link?.formation || selectedLineup?.formation || '—'}</h2>
              </div>
              <Users size={25} />
            </div>

            <div className="matchday-xi-grid">
              {startingXI.map((player) => (
                <div key={player.id} className="matchday-xi-player">
                  <span>
                    {player.photo_url
                      ? <img src={player.photo_url} alt="" />
                      : <ClubLogo club="Polonia Glasgow" className="matchday-xi-placeholder" />}
                  </span>
                  <div>
                    <strong>#{player.squad_number ?? '—'} {player.name}</strong>
                    <small>{player.position || '—'}</small>
                  </div>
                  {player.id === link?.captainId && <b>C</b>}
                  {player.id === link?.viceCaptainId && <b>VC</b>}
                </div>
              ))}
              {!startingXI.length && <p className="muted">{t.noSavedLineups}</p>}
            </div>
          </section>

          <section className="matchday-action-rail">
            <button className="metal-card matchday-action-card" type="button" onClick={() => onNavigate('lineup')}>
              <ShieldCheck size={24} />
              <div><strong>{t.lineup}</strong><small>{t.openTacticalBoard}</small></div>
              <ChevronRight size={18} />
            </button>
            <button
              className="metal-card matchday-action-card"
              type="button"
              onClick={() => {
                try { localStorage.setItem('ecc_match_centre_requested_view', 'live'); } catch {}
                onNavigate('matches');
              }}
            >
              <Clock3 size={24} />
              <div><strong>{t.liveMatch}</strong><small>{t.openLiveMatch}</small></div>
              <ChevronRight size={18} />
            </button>
            <button
              className="metal-card matchday-action-card"
              type="button"
              onClick={() => document.getElementById('matchday-media-studio')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >
              <ImageDown size={24} />
              <div><strong>{t.matchdayGraphic}</strong><small>{t.mediaStudioBelow}</small></div>
              <ChevronRight size={18} />
            </button>
          </section>

          <div id="matchday-media-studio">
          <MatchdayMediaStudio
            team={team}
            players={players}
            matches={matches}
            lineups={lineups}
            currentFormation={selectedLineup?.formation || '4-3-3'}
            currentSlots={(selectedLineup?.slots || {}) as Record<string, string | null>}
          />
          </div>
        </>
      ) : (
        <section className="metal-card empty-panel">
          <Swords />
          <p>{t.noMatches}</p>
        </section>
      )}
    </div>
  );
}
